import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  Fragment,
} from "react";
import { MenuItem, Chip } from "@material-ui/core";
import CustomTextField from "../../../components/Common/CustomTextField";
import { IoIosAdd, IoIosClose, IoIosRemove } from "react-icons/io";
import {
  individualDummyData,
  organisationDummyData,
} from "../../../constants";
import * as actions from "./actions";
import { getCurrentUserOrganizationId, toastSuccess } from "../../../utils";
import { useDispatch } from "react-redux";
import * as commonActions from "../../Common/actions";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import { validateDetails } from "./addDetailsValidation";
import CsvDownloader from "../../../components/Common/CsvDownloader";
import { CsvUploader } from "../../../components/Common/CsvUploader";
import { LuInfo } from "react-icons/lu";
import Tooltip from "../../../common/Tooltip";
import { FormField } from "../../../components/Common/FormInput";

const AddUpdateDetails = forwardRef((props, ref) => {
  const {
    setLoader = () => { },
    onCloseModal = () => { },
    getAllSavedResults = () => { },
    singleResults = {},
    isEdit = false,
    isReadOnly = false,
    type = "user",
  } = props;

  const dispatch = useDispatch();
  const [currentData, setCurrentData] = useState(isEdit ? singleResults : {});
  const [selectedValue, setSelectedValue] = useState(
    isEdit ? singleResults?.type : "Individual"
  );

  const handleChange = (event) => {
    setSelectedValue(event.target.value);
  };

  const onOpenErrorAlert = (errors = []) => {
    dispatch(commonActions.openErrorAlert({ errorList: errors }));
  };

  useImperativeHandle(ref, () => ({
    async onSubmitQuery() {
      const { errors, isValid } = validateDetails(currentData, selectedValue);
      if (!isValid) return onOpenErrorAlert(errors);
      setLoader(true);

      if (isEdit) {
        try {
          let newQuery = {
            ...currentData,
            type: selectedValue,
            individual_gender: currentData?.individual_gender || "male",
            individual_contactType:
              currentData?.individual_contactType || undefined,
          };

          actions
            .updateAddressDetails(newQuery)
            .then(() => {
              toastSuccess("Details Updated successfully");
              onCloseModal();
              getAllSavedResults();
              setLoader(false);
            })
            .catch(() => {
              setLoader(false);
            });
        } catch (ex) {
          setLoader(false);
        }
        onCloseModal();
        return;
      }

      let newQuery = {
        ...currentData,
        type: selectedValue,
        individual_gender: currentData?.individual_gender || "male",
        organizationId: type === "organization" ? getCurrentUserOrganizationId() : undefined,
        addressBookType: type,
      };

      actions
        .addAddressDetails(newQuery)
        .then(() => {
          toastSuccess("Details added successfully");
          onCloseModal();
          getAllSavedResults();
          setLoader(false);
        })
        .catch(() => {
          setLoader(false);
        });
    },
  }));

  const onChangeHandler = (e) => {
    const { value = "", name = "" } = e.target || {};
    setCurrentData({ ...currentData, [name]: value });
  };

  const onChangeHandlerForMultipleSelect = (e) => {
    const { value = [], name = "" } = e.target || {};
    const selectedValues = Array.isArray(value) ? value : value.split(",");
    setCurrentData((prevData) => ({
      ...prevData,
      [name]: selectedValues,
    }));
  };

  const handleCsvData = (structuredData) => {
    setCurrentData({ ...currentData, ...structuredData });
  };

  const handleDeleteChip = (event, chipToDelete, type) => {
    event.stopPropagation();
    let updatedValue;
    if (type === "individual") {
      updatedValue = individual_contactType.filter((value) => value !== chipToDelete);
    } else if (type === "organisation") {
      updatedValue = organisation_contactType.filter((value) => value !== chipToDelete);
    }

    onChangeHandlerForMultipleSelect({
      target: {
        name: type === "individual" ? "individual_contactType" : "organisation_contactType",
        value: updatedValue,
      },
    });
  };

  const {
    individual_title = "",
    individual_firstName = "",
    individual_middleName = "",
    individual_lastName = "",
    individual_webPage = "",
    individual_gender = "male",
    individual_birthdate = null,
    individual_anniversary = "",
    individual_location = "",
    individual_language = "",
    individual_emailPersonal = "",
    individual_emailPersonal2 = "",
    individual_mobilePersonal = "",
    individual_mobilePersonal2 = "",
    individual_homeAddress = "",
    individual_personal_city = "",
    individual_personal_state = "",
    individual_personal_postalCode = "",
    individual_personal_country = "",
    individual_shortName = "",
    individual_message = "",
    individual_contactType = [],
    individual_extraBankInfo = [{ label: "", value: "" }],
    individual_company = "",
    individual_jobTitle = "",
    individual_department = "",
    individual_officeLocation = "",
    individual_mobileNoOfficial = "",
    individual_mobileNoOfficial2 = "",
    individual_emailOfficial = "",
    individual_emailOfficial2 = "",
    individual_emailOfficial3 = "",
    individual_officeAddress = "",
    individual_professional_city = "",
    individual_professional_state = "",
    individual_professional_postalCode = "",
    individual_professional_country = "",
    individual_bankHolderName = "",
    individual_bankAccountNumber = "",
    individual_branchName = "",
    organisation_fullName = "",
    organisation_fullStyle = "",
    organisation_typeTags = "",
    organisation_officeLocation = "",
    organisation_contactPerson = "",
    organisation_contactPerson2 = "",
    organisation_mobileOfficial = "",
    organisation_mobileOfficial2 = "",
    organisation_officeAddress = "",
    organisation_emailOfficial = "",
    organisation_emailOfficial2 = "",
    organisation_emailOfficial3 = "",
    organisation_city = "",
    organisation_state = "",
    organisation_postalCode = "",
    organisation_country = "",
    organisation_shortName = "",
    organisation_notes = "",
    organisation_bankHolderName = "",
    organisation_bankAccountNumber = "",
    organisation_branchName = "",
    organisation_extraBankInfo = [{ label: "", value: "" }],
    organisation_contactType = [],
  } = currentData || {};

  const addDynamicField = (idx, key) => {
    const newField = { label: "", value: "" };
    setCurrentData((prevState) => {
      const arr = prevState[key] || [];
      const newArr = [
        ...arr.slice(0, idx + 1),
        newField,
        ...arr.slice(idx + 1),
      ];
      return {
        ...prevState,
        [key]: newArr,
      };
    });
  };

  const removeDynamicField = (idx, key) => {
    const arr = key === "individual_extraBankInfo" ? individual_extraBankInfo : organisation_extraBankInfo;
    if (arr.length === 1) return;
    const newArr = arr.filter((_, index) => index !== idx);
    setCurrentData((prevState) => ({
      ...prevState,
      [key]: newArr,
    }));
  };

  const handleDynamicChange = (idx, type, value, key) => {
    const arr = key === "individual_extraBankInfo" ? individual_extraBankInfo : organisation_extraBankInfo;
    const newArr = arr.map((field, index) =>
      index === idx ? { ...field, [type]: value } : field
    );
    setCurrentData((prevState) => ({
      ...prevState,
      [key]: newArr,
    }));
  };

  const inputClass = "w-full h-[20px] px-1.5 py-0 text-[11px] bg-[#ffffd6] text-slate-900 border border-slate-300 rounded-none focus:bg-white focus:ring-1 focus:ring-amber-600 outline-none";
  const selectClass = "w-full h-[20px] px-1 py-0 text-[11px] bg-[#ffffd6] text-slate-900 border border-slate-300 rounded-none focus:bg-white focus:ring-1 focus:ring-amber-600 outline-none cursor-pointer";
  const sectionHeaderClass = "bg-[#f0f2f5] border border-slate-300 px-2 py-0.5 text-[11px] font-semibold text-slate-800 capitalize tracking-wider mb-1 flex items-center justify-between";

  return (
    <Fragment>
      <div className="w-full text-[11px]">
        {/* Header Controls */}
        <div className="w-full flex justify-between items-center mb-2 pb-1 border-b border-slate-200">
          <RadioGroup row value={selectedValue} onChange={handleChange} className="flex items-center">
            <FormControlLabel
              value="Individual"
              control={<Radio size="small" color="primary" style={{ padding: 2 }} />}
              label={<span className="text-[11px] font-semibold text-slate-800">Individual</span>}
              style={{ marginRight: 12, marginBottom: 0 }}
            />
            <FormControlLabel
              value="Organisation"
              control={<Radio size="small" color="primary" style={{ padding: 2 }} />}
              label={<span className="text-[11px] font-semibold text-slate-800">Organisation</span>}
              style={{ marginRight: 0, marginBottom: 0 }}
            />
          </RadioGroup>
          <div className="flex items-center gap-2">
            {!isReadOnly && (
              <>
                <CsvDownloader
                  target="form"
                  data={
                    selectedValue === "Individual"
                      ? individualDummyData
                      : organisationDummyData
                  }
                />
                <CsvUploader onDataProcessed={handleCsvData} />
              </>
            )}
          </div>
        </div>

        {selectedValue === "Individual" ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5 w-full">
            {/* Personal Info Column */}
            <div className="space-y-1 w-full">
              <div className={sectionHeaderClass}>
                <span>Personal Info</span>
              </div>

              <FormField label="Title" required>
                <input
                  type="text"
                  name="individual_title"
                  value={individual_title}
                  onChange={onChangeHandler}
                  placeholder="Title"
                  className={inputClass}
                />
              </FormField>

              <FormField label="First Name" required>
                <input
                  type="text"
                  name="individual_firstName"
                  value={individual_firstName}
                  onChange={onChangeHandler}
                  placeholder="First Name"
                  className={inputClass}
                />
              </FormField>

              <FormField label="Middle">
                <input
                  type="text"
                  name="individual_middleName"
                  value={individual_middleName}
                  onChange={onChangeHandler}
                  placeholder="Middle Name"
                  className={inputClass}
                />
              </FormField>

              <FormField label="Last Name">
                <input
                  type="text"
                  name="individual_lastName"
                  value={individual_lastName}
                  onChange={onChangeHandler}
                  placeholder="Last Name"
                  className={inputClass}
                />
              </FormField>

              <FormField label="Website">
                <input
                  type="url"
                  name="individual_webPage"
                  value={individual_webPage}
                  onChange={onChangeHandler}
                  placeholder="Website"
                  className={inputClass}
                />
              </FormField>

              <FormField label="Gender">
                <select
                  name="individual_gender"
                  value={individual_gender}
                  onChange={onChangeHandler}
                  className={selectClass}
                >
                  <option value="">Select</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </FormField>

              <FormField label="Birth Date">
                <input
                  type="date"
                  name="individual_birthdate"
                  value={individual_birthdate || ""}
                  onChange={onChangeHandler}
                  className={selectClass}
                />
              </FormField>

              <FormField label="Anniversary">
                <input
                  type="date"
                  name="individual_anniversary"
                  value={individual_anniversary || ""}
                  onChange={onChangeHandler}
                  className={selectClass}
                />
              </FormField>

              <FormField label="Location">
                <input
                  type="text"
                  name="individual_location"
                  value={individual_location}
                  onChange={onChangeHandler}
                  placeholder="Location"
                  className={inputClass}
                />
              </FormField>

              <FormField label="Language">
                <input
                  type="text"
                  name="individual_language"
                  value={individual_language}
                  onChange={onChangeHandler}
                  placeholder="Language"
                  className={inputClass}
                />
              </FormField>

              <FormField label="Email" required>
                <input
                  type="email"
                  name="individual_emailPersonal"
                  value={individual_emailPersonal}
                  onChange={onChangeHandler}
                  placeholder="Personal email"
                  className={inputClass}
                />
              </FormField>

              <FormField label="Email 2">
                <input
                  type="email"
                  name="individual_emailPersonal2"
                  value={individual_emailPersonal2}
                  onChange={onChangeHandler}
                  placeholder="Secondary email"
                  className={inputClass}
                />
              </FormField>

              <FormField label="Mobile">
                <input
                  type="tel"
                  name="individual_mobilePersonal"
                  value={individual_mobilePersonal}
                  onChange={onChangeHandler}
                  placeholder="Mobile number"
                  className={inputClass}
                />
              </FormField>

              <FormField label="Mobile 2">
                <input
                  type="tel"
                  name="individual_mobilePersonal2"
                  value={individual_mobilePersonal2}
                  onChange={onChangeHandler}
                  placeholder="Secondary mobile"
                  className={inputClass}
                />
              </FormField>

              <FormField label="Address">
                <input
                  type="text"
                  name="individual_homeAddress"
                  value={individual_homeAddress}
                  onChange={onChangeHandler}
                  placeholder="Home address"
                  className={inputClass}
                />
              </FormField>

              <FormField label="City">
                <input
                  type="text"
                  name="individual_personal_city"
                  value={individual_personal_city}
                  onChange={onChangeHandler}
                  placeholder="City"
                  className={inputClass}
                />
              </FormField>

              <FormField label="State">
                <input
                  type="text"
                  name="individual_personal_state"
                  value={individual_personal_state}
                  onChange={onChangeHandler}
                  placeholder="State"
                  className={inputClass}
                />
              </FormField>

              <FormField label="Postal">
                <input
                  type="text"
                  name="individual_personal_postalCode"
                  value={individual_personal_postalCode}
                  onChange={onChangeHandler}
                  placeholder="Postal code"
                  className={inputClass}
                />
              </FormField>

              <FormField label="Country">
                <input
                  type="text"
                  name="individual_personal_country"
                  value={individual_personal_country}
                  onChange={onChangeHandler}
                  placeholder="Country"
                  className={inputClass}
                />
              </FormField>

              <FormField label="Short Name" required>
                <input
                  type="text"
                  name="individual_shortName"
                  value={individual_shortName}
                  onChange={onChangeHandler}
                  placeholder="Short name"
                  className={inputClass}
                />
              </FormField>

              <FormField label="Notes">
                <textarea
                  name="individual_message"
                  value={individual_message}
                  onChange={onChangeHandler}
                  placeholder="Remarks..."
                  rows={2}
                  className="w-full px-1.5 py-0.5 text-[11px] bg-[#ffffd6] text-slate-900 border border-slate-300 rounded-none focus:bg-white focus:ring-1 focus:ring-amber-600 outline-none resize-none"
                />
              </FormField>
            </div>

            {/* Professional Info Column */}
            <div className="space-y-1 w-full">
              <div className={sectionHeaderClass}>
                <span>Professional Info</span>
              </div>

              <FormField label="Company">
                <input
                  type="text"
                  name="individual_company"
                  value={individual_company}
                  onChange={onChangeHandler}
                  placeholder="Company"
                  className={inputClass}
                />
              </FormField>

              <FormField label="Job Title">
                <input
                  type="text"
                  name="individual_jobTitle"
                  value={individual_jobTitle}
                  onChange={onChangeHandler}
                  placeholder="Job title"
                  className={inputClass}
                />
              </FormField>

              <FormField label="Department">
                <input
                  type="text"
                  name="individual_department"
                  value={individual_department}
                  onChange={onChangeHandler}
                  placeholder="Department"
                  className={inputClass}
                />
              </FormField>

              <FormField label="Office Loc">
                <input
                  type="text"
                  name="individual_officeLocation"
                  value={individual_officeLocation}
                  onChange={onChangeHandler}
                  placeholder="Office location"
                  className={inputClass}
                />
              </FormField>

              <FormField label="Mobile Off">
                <input
                  type="tel"
                  name="individual_mobileNoOfficial"
                  value={individual_mobileNoOfficial}
                  onChange={onChangeHandler}
                  placeholder="Official mobile"
                  className={inputClass}
                />
              </FormField>

              <FormField label="Mobile Off 2">
                <input
                  type="tel"
                  name="individual_mobileNoOfficial2"
                  value={individual_mobileNoOfficial2}
                  onChange={onChangeHandler}
                  placeholder="Official mobile 2"
                  className={inputClass}
                />
              </FormField>

              <FormField label="Email Off">
                <input
                  type="email"
                  name="individual_emailOfficial"
                  value={individual_emailOfficial}
                  onChange={onChangeHandler}
                  placeholder="Official email"
                  className={inputClass}
                />
              </FormField>

              <FormField label="Email Off 2">
                <input
                  type="email"
                  name="individual_emailOfficial2"
                  value={individual_emailOfficial2}
                  onChange={onChangeHandler}
                  placeholder="Official email 2"
                  className={inputClass}
                />
              </FormField>

              <FormField label="Email Off 3">
                <input
                  type="email"
                  name="individual_emailOfficial3"
                  value={individual_emailOfficial3}
                  onChange={onChangeHandler}
                  placeholder="Official email 3"
                  className={inputClass}
                />
              </FormField>

              <FormField label="Office Addr">
                <input
                  type="text"
                  name="individual_officeAddress"
                  value={individual_officeAddress}
                  onChange={onChangeHandler}
                  placeholder="Office address"
                  className={inputClass}
                />
              </FormField>

              <FormField label="City">
                <input
                  type="text"
                  name="individual_professional_city"
                  value={individual_professional_city}
                  onChange={onChangeHandler}
                  placeholder="City"
                  className={inputClass}
                />
              </FormField>

              <FormField label="State">
                <input
                  type="text"
                  name="individual_professional_state"
                  value={individual_professional_state}
                  onChange={onChangeHandler}
                  placeholder="State"
                  className={inputClass}
                />
              </FormField>

              <FormField label="Postal">
                <input
                  type="text"
                  name="individual_professional_postalCode"
                  value={individual_professional_postalCode}
                  onChange={onChangeHandler}
                  placeholder="Postal code"
                  className={inputClass}
                />
              </FormField>

              <FormField label="Country">
                <input
                  type="text"
                  name="individual_professional_country"
                  value={individual_professional_country}
                  onChange={onChangeHandler}
                  placeholder="Country"
                  className={inputClass}
                />
              </FormField>

              <FormField label="Contact Type">
                <CustomTextField
                  select
                  className="compact-select-field"
                  name="individual_contactType"
                  value={
                    individual_contactType.length > 0
                      ? individual_contactType
                      : []
                  }
                  onChange={onChangeHandlerForMultipleSelect}
                  fullWidth
                  SelectProps={{
                    multiple: true,
                    displayEmpty: true,
                    MenuProps: {
                      getContentAnchorEl: null,
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      PaperProps: {
                        style: { maxHeight: 180 },
                        className: "compact-dropdown-menu",
                      },
                    },
                    renderValue: (selected) => {
                      if (!selected.length) {
                        return (
                          <span style={{ color: "#aaa" }}>
                            Select contact type
                          </span>
                        );
                      }
                      return (
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 2,
                            alignItems: "center",
                          }}
                        >
                          {selected.map((value) => (
                            <Chip
                              key={value}
                              label={value}
                              variant="outlined"
                              size="small"
                              style={{ height: 16, fontSize: 10, margin: "1px 0", backgroundColor: "#fff" }}
                              deleteIcon={
                                <IoIosClose size={12} color="rose" />
                              }
                              onDelete={(event) =>
                                handleDeleteChip(event, value, "individual")
                              }
                              onMouseDown={(event) =>
                                handleDeleteChip(event, value, "individual")
                              }
                            />
                          ))}
                        </div>
                      );
                    },
                  }}
                >
                  <MenuItem disabled value="" dense style={{ fontSize: 11, minHeight: 22, height: 22, padding: "2px 8px" }}>
                    <em>Select contact type</em>
                  </MenuItem>
                  {[
                    "Shipping Agents",
                    "Own Entities",
                    "Brokers",
                    "Charterers",
                    "Ship Owners",
                    "Fuel Suppliers",
                    "Others",
                    "Port Services Supplier",
                    "Port/Terminal",
                    "Receiver",
                    "Shipper",
                  ].map((option) => (
                    <MenuItem key={option} value={option} dense style={{ fontSize: 11, minHeight: 22, height: 22, padding: "2px 8px" }}>
                      {option}
                    </MenuItem>
                  ))}
                </CustomTextField>
              </FormField>
            </div>

            {/* Bank Details Column */}
            <div className="space-y-1 w-full">
              <div className={sectionHeaderClass}>
                <div className="flex items-center gap-1">
                  <span>Bank Details</span>
                  <Tooltip position="left" text="These details will be shown on invoices when this selected as an counterparty in Operation.">
                    <LuInfo size={13} className="text-amber-500 cursor-pointer" />
                  </Tooltip>
                </div>
              </div>

              <FormField label="Holder">
                <input
                  type="text"
                  name="individual_bankHolderName"
                  value={individual_bankHolderName}
                  onChange={onChangeHandler}
                  placeholder="Account holder"
                  className={inputClass}
                />
              </FormField>

              <FormField label="Account">
                <input
                  type="text"
                  name="individual_bankAccountNumber"
                  value={individual_bankAccountNumber}
                  onChange={onChangeHandler}
                  placeholder="Account number"
                  className={inputClass}
                />
              </FormField>

              <FormField label="Branch">
                <input
                  type="text"
                  name="individual_branchName"
                  value={individual_branchName}
                  onChange={onChangeHandler}
                  placeholder="Branch name"
                  className={inputClass}
                />
              </FormField>

              <div className="mt-1 pt-1 border-t border-slate-200">
                <div className="text-[11px] font-semibold text-slate-700 mb-1">Extra Details</div>
                <div className="space-y-1">
                  {individual_extraBankInfo.map((field, index) => (
                    <div key={index} className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => addDynamicField(index, "individual_extraBankInfo")}
                        className="w-5 h-[20px] flex items-center justify-center text-[#0051b6] hover:bg-sky-50 border border-slate-300 bg-white cursor-pointer shrink-0"
                        title="Insert field here"
                        disabled={isReadOnly}
                      >
                        <IoIosAdd size={14} />
                      </button>
                      <input
                        type="text"
                        value={field.label}
                        onChange={(e) => handleDynamicChange(index, "label", e.target.value, "individual_extraBankInfo")}
                        placeholder="Label"
                        className="w-1/2 h-[20px] px-1.5 py-0 text-[11px] bg-[#ffffd6] text-slate-900 border border-slate-300 rounded-none focus:bg-white focus:ring-1 focus:ring-amber-600 outline-none"
                      />
                      <input
                        type="text"
                        value={field.value}
                        onChange={(e) => handleDynamicChange(index, "value", e.target.value, "individual_extraBankInfo")}
                        placeholder="Value"
                        className="w-1/2 h-[20px] px-1.5 py-0 text-[11px] bg-[#ffffd6] text-slate-900 border border-slate-300 rounded-none focus:bg-white focus:ring-1 focus:ring-amber-600 outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => removeDynamicField(index, "individual_extraBankInfo")}
                        className="w-5 h-[20px] flex items-center justify-center text-rose-500 hover:bg-rose-50 border border-slate-300 bg-white cursor-pointer shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={individual_extraBankInfo.length === 1 || isReadOnly}
                        title="Delete field"
                      >
                        <IoIosRemove size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Organisation View */
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5 w-full">
            {/* Column 1: Basic Info */}
            <div className="space-y-1 w-full">
              <div className={sectionHeaderClass}>
                <span>Organisation Info</span>
              </div>

              <FormField label="Full Name" required>
                <input
                  type="text"
                  name="organisation_fullName"
                  value={organisation_fullName}
                  onChange={onChangeHandler}
                  placeholder="Enter Full Name"
                  className={inputClass}
                />
              </FormField>

              <FormField label="Full Style">
                <input
                  type="text"
                  name="organisation_fullStyle"
                  value={organisation_fullStyle}
                  onChange={onChangeHandler}
                  placeholder="Enter Full Style"
                  className={inputClass}
                />
              </FormField>

              <FormField label="Type/Tags">
                <input
                  type="text"
                  name="organisation_typeTags"
                  value={organisation_typeTags}
                  onChange={onChangeHandler}
                  placeholder="Enter Type/Tags"
                  className={inputClass}
                />
              </FormField>

              <FormField label="Office Loc">
                <input
                  type="text"
                  name="organisation_officeLocation"
                  value={organisation_officeLocation}
                  onChange={onChangeHandler}
                  placeholder="Enter Office Location"
                  className={inputClass}
                />
              </FormField>

              <FormField label="Contact Person">
                <input
                  type="text"
                  name="organisation_contactPerson"
                  value={organisation_contactPerson}
                  onChange={onChangeHandler}
                  placeholder="Enter Contact Person"
                  className={inputClass}
                />
              </FormField>

              <FormField label="Contact Person 2">
                <input
                  type="text"
                  name="organisation_contactPerson2"
                  value={organisation_contactPerson2}
                  onChange={onChangeHandler}
                  placeholder="Enter Contact Person 2"
                  className={inputClass}
                />
              </FormField>

              <FormField label="Mobile Off">
                <input
                  type="tel"
                  name="organisation_mobileOfficial"
                  value={organisation_mobileOfficial}
                  onChange={onChangeHandler}
                  placeholder="Official mobile"
                  className={inputClass}
                />
              </FormField>

              <FormField label="Mobile Off 2">
                <input
                  type="tel"
                  name="organisation_mobileOfficial2"
                  value={organisation_mobileOfficial2}
                  onChange={onChangeHandler}
                  placeholder="Official mobile 2"
                  className={inputClass}
                />
              </FormField>

              <FormField label="Office Addr">
                <input
                  type="text"
                  name="organisation_officeAddress"
                  value={organisation_officeAddress}
                  onChange={onChangeHandler}
                  placeholder="Office address"
                  className={inputClass}
                />
              </FormField>
            </div>

            {/* Column 2: Contact & Location */}
            <div className="space-y-1 w-full">
              <div className={sectionHeaderClass}>
                <span>Contact & Location</span>
              </div>

              <FormField label="Email Off" required>
                <input
                  type="email"
                  name="organisation_emailOfficial"
                  value={organisation_emailOfficial}
                  onChange={onChangeHandler}
                  placeholder="Official email"
                  className={inputClass}
                />
              </FormField>

              <FormField label="Email Off 2">
                <input
                  type="email"
                  name="organisation_emailOfficial2"
                  value={organisation_emailOfficial2}
                  onChange={onChangeHandler}
                  placeholder="Official email 2"
                  className={inputClass}
                />
              </FormField>

              <FormField label="Email Off 3">
                <input
                  type="email"
                  name="organisation_emailOfficial3"
                  value={organisation_emailOfficial3}
                  onChange={onChangeHandler}
                  placeholder="Official email 3"
                  className={inputClass}
                />
              </FormField>

              <FormField label="City">
                <input
                  type="text"
                  name="organisation_city"
                  value={organisation_city}
                  onChange={onChangeHandler}
                  placeholder="City"
                  className={inputClass}
                />
              </FormField>

              <FormField label="State">
                <input
                  type="text"
                  name="organisation_state"
                  value={organisation_state}
                  onChange={onChangeHandler}
                  placeholder="State"
                  className={inputClass}
                />
              </FormField>

              <FormField label="Postal Code">
                <input
                  type="text"
                  name="organisation_postalCode"
                  value={organisation_postalCode}
                  onChange={onChangeHandler}
                  placeholder="Postal Code"
                  className={inputClass}
                />
              </FormField>

              <FormField label="Country">
                <input
                  type="text"
                  name="organisation_country"
                  value={organisation_country}
                  onChange={onChangeHandler}
                  placeholder="Country"
                  className={inputClass}
                />
              </FormField>

              <FormField label="Short Name" required>
                <input
                  type="text"
                  name="organisation_shortName"
                  value={organisation_shortName || ""}
                  onChange={onChangeHandler}
                  placeholder="Enter Short Name"
                  className={inputClass}
                />
              </FormField>

              <FormField label="Contact Type">
                <CustomTextField
                  select
                  className="compact-select-field"
                  name="organisation_contactType"
                  value={
                    organisation_contactType.length > 0
                      ? organisation_contactType
                      : []
                  }
                  onChange={onChangeHandlerForMultipleSelect}
                  fullWidth
                  SelectProps={{
                    multiple: true,
                    displayEmpty: true,
                    MenuProps: {
                      getContentAnchorEl: null,
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      PaperProps: {
                        style: { maxHeight: 180 },
                        className: "compact-dropdown-menu",
                      },
                    },
                    renderValue: (selected) => {
                      if (!selected?.length) {
                        return (
                          <span style={{ color: "#aaa" }}>
                            Select contact type
                          </span>
                        );
                      }
                      return (
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 2,
                            alignItems: "center",
                          }}
                        >
                          {selected?.map((value) => (
                            <Chip
                              key={value}
                              label={value}
                              variant="outlined"
                              size="small"
                              style={{ height: 16, fontSize: 10, margin: "1px 0", backgroundColor: "#fff" }}
                              deleteIcon={
                                <IoIosClose size={12} color="rose" />
                              }
                              onDelete={(event) =>
                                handleDeleteChip(event, value, "organisation")
                              }
                              onMouseDown={(event) =>
                                handleDeleteChip(event, value, "organisation")
                              }
                            />
                          ))}
                        </div>
                      );
                    },
                  }}
                >
                  <MenuItem disabled value="" dense style={{ fontSize: 11, minHeight: 22, height: 22, padding: "2px 8px" }}>
                    <em>Select contact type</em>
                  </MenuItem>
                  {[
                    "Shipping Agents",
                    "Own Entities",
                    "Brokers",
                    "Charterers",
                    "Ship Owners",
                    "Fuel Suppliers",
                    "Others",
                    "Port Services Supplier",
                    "Port/Terminal",
                    "Receiver",
                    "Shipper",
                  ].map((option) => (
                    <MenuItem key={option} value={option} dense style={{ fontSize: 11, minHeight: 22, height: 22, padding: "2px 8px" }}>
                      {option}
                    </MenuItem>
                  ))}
                </CustomTextField>
              </FormField>

              <FormField label="Notes">
                <textarea
                  name="organisation_notes"
                  value={organisation_notes}
                  onChange={onChangeHandler}
                  placeholder="Please enter your remarks here..."
                  rows={2}
                  className="w-full px-1.5 py-0.5 text-[11px] bg-[#ffffd6] text-slate-900 border border-slate-300 rounded-none focus:bg-white focus:ring-1 focus:ring-amber-600 outline-none resize-none"
                />
              </FormField>
            </div>

            {/* Column 3: Bank Details */}
            <div className="space-y-1 w-full">
              <div className={sectionHeaderClass}>
                <div className="flex items-center gap-1">
                  <span>Bank Details</span>
                  <Tooltip position="left" text="These details will be shown on invoices when this selected as an counterparty in Operation.">
                    <LuInfo size={13} className="text-amber-500 cursor-pointer" />
                  </Tooltip>
                </div>
              </div>

              <FormField label="Holder">
                <input
                  type="text"
                  name="organisation_bankHolderName"
                  value={organisation_bankHolderName}
                  onChange={onChangeHandler}
                  placeholder="Enter Account Holder Name"
                  className={inputClass}
                />
              </FormField>

              <FormField label="Account">
                <input
                  type="text"
                  name="organisation_bankAccountNumber"
                  value={organisation_bankAccountNumber}
                  onChange={onChangeHandler}
                  placeholder="Enter Bank Account Number"
                  className={inputClass}
                />
              </FormField>

              <FormField label="Branch">
                <input
                  type="text"
                  name="organisation_branchName"
                  value={organisation_branchName}
                  onChange={onChangeHandler}
                  placeholder="Enter Bank Branch Name"
                  className={inputClass}
                />
              </FormField>

              <div className="mt-1 pt-1 border-t border-slate-200">
                <div className="text-[11px] font-semibold text-slate-700 mb-1">Extra Details</div>
                {organisation_extraBankInfo?.length > 0 ? (
                  <div className="space-y-1">
                    {organisation_extraBankInfo.map((field, idx) => (
                      <div key={idx} className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => addDynamicField(idx, "organisation_extraBankInfo")}
                          className="w-5 h-[20px] flex items-center justify-center text-[#0051b6] hover:bg-sky-50 border border-slate-300 bg-white cursor-pointer shrink-0"
                          title="Insert field here"
                          disabled={isReadOnly}
                        >
                          <IoIosAdd size={14} />
                        </button>
                        <input
                          type="text"
                          value={field.label}
                          onChange={(e) =>
                            handleDynamicChange(idx, "label", e.target.value, "organisation_extraBankInfo")
                          }
                          placeholder="Label"
                          className="w-1/2 h-[20px] px-1.5 py-0 text-[11px] bg-[#ffffd6] text-slate-900 border border-slate-300 rounded-none focus:bg-white focus:ring-1 focus:ring-amber-600 outline-none"
                        />
                        <input
                          type="text"
                          value={field.value}
                          onChange={(e) =>
                            handleDynamicChange(idx, "value", e.target.value, "organisation_extraBankInfo")}
                          placeholder="Value"
                          className="w-1/2 h-[20px] px-1.5 py-0 text-[11px] bg-[#ffffd6] text-slate-900 border border-slate-300 rounded-none focus:bg-white focus:ring-1 focus:ring-amber-600 outline-none"
                        />
                        <button
                          type="button"
                          onClick={() => removeDynamicField(idx, "organisation_extraBankInfo")}
                          className="w-5 h-[20px] flex items-center justify-center text-rose-500 hover:bg-rose-50 border border-slate-300 bg-white cursor-pointer shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={organisation_extraBankInfo.length === 1 || isReadOnly}
                          title="Delete field"
                        >
                          <IoIosRemove size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div>
                    <button
                      type="button"
                      onClick={() => addDynamicField(0, "organisation_extraBankInfo")}
                      className="h-[20px] px-2 text-[11px] flex items-center gap-1 bg-[#0051b6] text-white font-medium hover:bg-blue-700 cursor-pointer border-none"
                      disabled={isReadOnly}
                    >
                      <IoIosAdd size={14} /> <span>Add Extra Bank Details</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Fragment>
  );
});

export default AddUpdateDetails;
